import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, tokenStore, extractAxiosErrorMessage } from "./api";

const AuthContext = createContext(null);

// Phase 44.31.1 — Polling silenzioso /auth/me per detect rapido di sessione revocata
// (cambio password / logout globale / admin revoke da altro dispositivo).
// 20s è un compromesso tra reattività e carico backend.
const SESSION_POLL_MS = 20_000;

// Phase 44.31.3 — Throttle per il fail-fast check ad ogni route mount.
// Se l'ultimo check è < 3s fa, salta (evita raffiche durante navigation rapida).
const FAILFAST_THROTTLE_MS = 3_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  const lastCheckTsRef = useRef(0);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      userRef.current = data;
      lastCheckTsRef.current = Date.now();
    } catch {
      setUser(null);
      userRef.current = null;
      tokenStore.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  // Phase 44.31.3 — Check on-demand (fail-fast da ProtectedRoute al cambio route).
  // Throttled a 3s per evitare raffiche. L'interceptor 401 in api.js gestisce
  // poi il logout + redirect.
  const checkSession = useCallback(() => {
    if (!tokenStore.get()) return;
    const now = Date.now();
    if (now - lastCheckTsRef.current < FAILFAST_THROTTLE_MS) return;
    lastCheckTsRef.current = now;
    api.get("/auth/me").catch(() => {});
  }, []);

  useEffect(() => {
    if (tokenStore.get()) fetchMe();
    else setLoading(false);
  }, [fetchMe]);

  // Phase 44.31.1 — Listener globale "auth:revoked" (emesso dall'interceptor 401).
  // Forza logout immediato + redirect login + toast.
  useEffect(() => {
    const handler = (e) => {
      const reason = e?.detail?.reason || "invalid";
      // Evita doppio toast se è già scollegato
      if (!userRef.current && !tokenStore.get()) return;
      tokenStore.clear();
      setUser(null);
      userRef.current = null;
      const msg = reason === "revoked"
        ? "Sessione revocata. Accedi di nuovo."
        : reason === "expired"
          ? "Sessione scaduta. Accedi di nuovo."
          : "Sessione scaduta o revocata. Accedi di nuovo.";
      toast.error(msg, { id: "auth-revoked", duration: 6000 });
      // Redirect a /login se non già lì (preserva la route corrente come `next`)
      try {
        const here = window.location.pathname + window.location.search;
        if (!here.startsWith("/login")) {
          const next = encodeURIComponent(here);
          window.location.replace(`/login?next=${next}`);
        }
      } catch { /* noop */ }
    };
    window.addEventListener("auth:revoked", handler);
    return () => window.removeEventListener("auth:revoked", handler);
  }, []);

  // Phase 44.31.1 — Polling silenzioso /auth/me ogni 20s.
  // L'interceptor 401 si occupa del cleanup; qui basta tenere "vivo" il check.
  // Pausa quando la tab è in background per non sprecare risorse.
  useEffect(() => {
    if (!user) return undefined;
    let intervalId = null;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      if (!tokenStore.get()) return;
      // Ignora errore — l'interceptor gestisce il 401
      api.get("/auth/me").catch(() => {});
    };
    intervalId = setInterval(tick, SESSION_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      tokenStore.set(data.access_token);
      setUser(data.user);
      userRef.current = data.user;
      return { ok: true, user: data.user };
    } catch (e) {
      // Phase 43.40 — message mapping context-aware (401 → credenziali errate, ecc.)
      // Logghiamo i dettagli tecnici in console per debug remoto
      // eslint-disable-next-line no-console
      console.warn("[auth.login] failed", {
        status: e.response?.status,
        detail: e.response?.data?.detail,
        message: e.message,
      });
      return { ok: false, error: extractAxiosErrorMessage(e, "login") };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      tokenStore.set(data.access_token);
      setUser(data.user);
      userRef.current = data.user;
      return { ok: true, user: data.user };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[auth.register] failed", {
        status: e.response?.status,
        detail: e.response?.data?.detail,
        message: e.message,
      });
      return { ok: false, error: extractAxiosErrorMessage(e, "register") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    tokenStore.clear();
    setUser(null);
    userRef.current = null;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
